import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTachometerAlt, faCalendarAlt, faCog } from '@fortawesome/free-solid-svg-icons';
import './VehicleSearch.css';

const VehicleSearch = ({ searchType }) => {
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({
    make: '', model: '', cabType: '', wheelbase: '', derivative: '',
    price: '', year: '', mileage: '', gearbox: '', bodyType: '',
    colour: '', doors: '', seats: '', fuelType: '', batteryRange: '',
    chargingTime: '', engineSize: '', enginePower: '', acceleration: '',
    fuelConsumption: '', co2Emissions: '', taxPerYear: '', insuranceGroup: '',
    driveType: '', vatStatus: '',
  });
  const [sortOrder, setSortOrder] = useState('relevance');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const filterLabels = {
    make: 'Make and model',
    price: 'Price',
    year: 'Year',
    mileage: 'Mileage',
    gearbox: 'Gearbox',
    bodyType: 'Body type',
  };

  const [distinct, setDistinct] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Load initial filters from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initial = {};
    for (const [k, v] of params.entries()) {
      initial[k] = v;
    }
    setFilters(f => ({ ...f, ...initial }));
    setFiltersLoaded(true);
  }, [location.search]);

  // Fetch distinct filter options
  useEffect(() => {
    const fetchDistinct = async () => {
      const qs = Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      try {
        const r = await axios.get(`/api/vehicles/distinct/${searchType}?${qs}`);
        setDistinct(r.data);
      } catch (e) {
        console.error('Error fetching distinct:', e);
      }
    };
    fetchDistinct();
  }, [searchType, filters]);

  // Fetch vehicles based on filters & sort
  useEffect(() => {
    if (!filtersLoaded) return;
    const fetchVehicles = async () => {
      const qs = Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      const sortQ = sortOrder ? `&sort=${sortOrder}` : '';
      try {
        const r = await axios.get(`/api/vehicles/search/${searchType}?${qs}${sortQ}`);
        setVehicles(r.data);
      } catch (e) {
        console.error('Error fetching vehicles:', e);
      }
    };
    fetchVehicles();
  }, [filters, sortOrder, searchType, filtersLoaded]);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    // 1) Build the new filters object
    const newFilters = { ...filters, [name]: value };
    // 2) Update component state
    setFilters(newFilters);
    // 3) Mirror that state into the URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    // Replace current URL’s ?... with the updated params
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleSortChange = e => setSortOrder(e.target.value);
  const clearAllFilters = () => {
    setFilters({
      make: '',
      model: '',
      cabType: '',
      wheelbase: '',
      derivative: '',
      price: '',
      year: '',
      mileage: '',
      gearbox: '',
      bodyType: '',
      colour: '',
      doors: '',
      seats: '',
      fuelType: '',
      batteryRange: '',
      chargingTime: '',
      engineSize: '',
      enginePower: '',
      acceleration: '',
      fuelConsumption: '',
      co2Emissions: '',
      taxPerYear: '',
      insuranceGroup: '',
      driveType: '',
      vatStatus: '',
    });
  };
  const hasValues = field => Array.isArray(distinct[field]) && distinct[field].length > 0;
  const isVanOrTruck = ['van', 'truck'].includes(searchType);

  // Scroll to section when opening modal
  useEffect(() => {
    if (filterModalOpen && activeSection) {
      const el = document.getElementById(`filter-${activeSection}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [filterModalOpen, activeSection]);

  return (
    <div className="vehicle-search">
      {/* Mobile filter chips */}
      <div className="mobile-filters-bar">
        {Object.entries(filterLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setActiveSection(key); setFilterModalOpen(true); }}
          >
            + {label}
          </button>
        ))}
        <button onClick={() => { setActiveSection(null); setFilterModalOpen(true); }}>
          Filter and sort
        </button>
      </div>

      {/* Search results */}
      <div className="results">
        {vehicles.map(v => (
          <Link to={`/vehicles/${v._id}`} key={v._id} className="vehicle-card">
            <div className="vehicle-card-image">
              <img src={v.media.images?.[0]?.href} alt={`${v.vehicle.make} ${v.vehicle.model}`} />
            </div>
            <div className="vehicle-card-content">
              <div className="card-price-row">
                <p className="price">£{v.adverts.retailAdverts.totalPrice.amountGBP}</p>
                {v.adverts.retailAdverts.vatStatus && (
                  <span className="vat-status">{v.adverts.retailAdverts.vatStatus}</span>
                )}
              </div>
              <h3 className="make-model">{v.vehicle.make} {v.vehicle.model}</h3>
              <p className="derivative">{v.vehicle.derivative}</p>
              <div className="card-icons-row">
                <div className="icon-item">
                  <FontAwesomeIcon icon={faTachometerAlt} />
                  <span>{v.vehicle.odometerReadingMiles} miles</span>
                </div>
                <div className="icon-item">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>{v.vehicle.yearOfManufacture}</span>
                </div>
                <div className="icon-item">
                  <FontAwesomeIcon icon={faCog} />
                  <span>{v.vehicle.transmissionType}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Mobile filter modal */}
      {filterModalOpen && (
        <div className="filter-modal-overlay" onClick={() => setFilterModalOpen(false)}>
          <div className="filter-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setFilterModalOpen(false)}>×</button>
            <div className="filter-panel">
              <h2>Filter and sort</h2>
              {/* Sort */}
              <div className="sort-section">
                <label htmlFor="sortOrder-modal">Sort by:</label>
                <select id="sortOrder-modal" name="sortOrder" onChange={handleSortChange} value={sortOrder}>
                  <option value="relevance">Relevance</option>
                  <option value="price_asc">Price (low to high)</option>
                  <option value="price_desc">Price (high to low)</option>
                  <option value="distance">Distance</option>
                  <option value="mileage">Mileage</option>
                  <option value="year_desc">Age (newest)</option>
                  <option value="year_asc">Age (oldest)</option>
                  <option value="most_recent">Most recent</option>
                </select>
              </div>
              {/* Filters */}
              <div className="filters">
                {hasValues('make') && (
                  <div className="filter-field" id="filter-make">
                    <label>Make</label>
                    <select name="make" onChange={handleFilterChange} value={filters.make}>
                      <option value="">Any</option>
                      {distinct.make.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('model') && (
                  <div className="filter-field" id="filter-model">
                    <label>Model</label>
                    <select name="model" onChange={handleFilterChange} value={filters.model}>
                      <option value="">Any</option>
                      {distinct.model.map(mod => <option key={mod} value={mod}>{mod}</option>)}
                    </select>
                  </div>
                )}
                {isVanOrTruck && hasValues('cabType') && (
                  <div className="filter-field" id="filter-cabType">
                    <label>Cab Type</label>
                    <select name="cabType" onChange={handleFilterChange} value={filters.cabType}>
                      <option value="">Any</option>
                      {distinct.cabType.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {isVanOrTruck && hasValues('wheelbase') && (
                  <div className="filter-field" id="filter-wheelbase">
                    <label>Wheelbase</label>
                    <select name="wheelbase" onChange={handleFilterChange} value={filters.wheelbase}>
                      <option value="">Any</option>
                      {distinct.wheelbase.map(wb => <option key={wb} value={wb}>{wb}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('derivative') && (
                  <div className="filter-field" id="filter-derivative">
                    <label>Derivative</label>
                    <select name="derivative" onChange={handleFilterChange} value={filters.derivative}>
                      <option value="">Any</option>
                      {distinct.derivative.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('price') && (
                  <div className="filter-field" id="filter-price">
                    <label>Price</label>
                    <select name="price" onChange={handleFilterChange} value={filters.price}>
                      <option value="">Any</option>
                      {distinct.price.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('year') && (
                  <div className="filter-field" id="filter-year">
                    <label>Year</label>
                    <select name="year" onChange={handleFilterChange} value={filters.year}>
                      <option value="">Any</option>
                      {distinct.year.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('mileage') && (
                  <div className="filter-field" id="filter-mileage">
                    <label>Mileage</label>
                    <select name="mileage" onChange={handleFilterChange} value={filters.mileage}>
                      <option value="">Any</option>
                      {distinct.mileage.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('gearbox') && (
                  <div className="filter-field" id="filter-gearbox">
                    <label>Gearbox</label>
                    <select name="gearbox" onChange={handleFilterChange} value={filters.gearbox}>
                      <option value="">Any</option>
                      {distinct.gearbox.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('bodyType') && (
                  <div className="filter-field" id="filter-bodyType">
                    <label>Body Type</label>
                    <select name="bodyType" onChange={handleFilterChange} value={filters.bodyType}>
                      <option value="">Any</option>
                      {distinct.bodyType.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('colour') && (
                  <div className="filter-field" id="filter-colour">
                    <label>Colour</label>
                    <select name="colour" onChange={handleFilterChange} value={filters.colour}>
                      <option value="">Any</option>
                      {distinct.colour.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('doors') && (
                  <div className="filter-field" id="filter-doors">
                    <label>Doors</label>
                    <select name="doors" onChange={handleFilterChange} value={filters.doors}>
                      <option value="">Any</option>
                      {distinct.doors.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('seats') && (
                  <div className="filter-field" id="filter-seats">
                    <label>Seats</label>
                    <select name="seats" onChange={handleFilterChange} value={filters.seats}>
                      <option value="">Any</option>
                      {distinct.seats.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('fuelType') && (
                  <div className="filter-field" id="filter-fuelType">
                    <label>Fuel Type</label>
                    <select name="fuelType" onChange={handleFilterChange} value={filters.fuelType}>
                      <option value="">Any</option>
                      {distinct.fuelType.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('batteryRange') && (
                  <div className="filter-field" id="filter-batteryRange">
                    <label>Battery Range</label>
                    <select name="batteryRange" onChange={handleFilterChange} value={filters.batteryRange}>
                      <option value="">Any</option>
                      {distinct.batteryRange.map(br => <option key={br} value={br}>{br}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('chargingTime') && (
                  <div className="filter-field" id="filter-chargingTime">
                    <label>Charging Time</label>
                    <select name="chargingTime" onChange={handleFilterChange} value={filters.chargingTime}>
                      <option value="">Any</option>
                      {distinct.chargingTime.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('engineSize') && (
                  <div className="filter-field" id="filter-engineSize">
                    <label>Engine Size</label>
                    <select name="engineSize" onChange={handleFilterChange} value={filters.engineSize}>
                      <option value="">Any</option>
                      {distinct.engineSize.map(es => <option key={es} value={es}>{es}</option>)}
                    </select>
                  </div>
                )}
                {hasValues('enginePower') && (
                  <div className="filter-field" id="filter-enginePower">
                    <label>Engine Power</label>
                    <select name="enginePower" onChange={handleFilterChange} value={filters.enginePower}>
                      <option value="">Any</option>
                      {distinct.enginePower.map(ep => <option key={ep} value={ep}>{ep}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            {/* Action buttons */}
            <div className="modal-actions">
              <button
                type="button"
                className="clear-all-button"
                onClick={clearAllFilters}
              >
                Clear all
              </button>
              <button
                type="button"
                className="search-button"
                onClick={() => setFilterModalOpen(false)}
              >
                Search {vehicles.length.toLocaleString()} {searchType === 'car' ? 'cars' : searchType === 'van' ? 'vans' : 'trucks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleSearch;